import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardHeader, ErrorState, Input, PageHeader, PageLoader, Select, Switch, Textarea, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { FormSection, KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { masterOptions, refreshMasters } from '../services/masterService';
import { useMasters } from '../hooks/useMasters';
import { sectionOf } from '../constants/masterSections';
import { masterService } from '../services/masterService';
import type { MasterEntry, MasterType } from '../types/masterTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';

/** Add / edit one entry of a master list. The stored value is fixed once saved; the name can change any time. */
export default function MasterForm() {
  const { type = '', id = '' } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const toast = useToast();
  useMasters();
  const section = sectionOf(type);
  const [saving, setSaving] = useState(false);
  const { form, setForm, set, errors, setErrors } = useForm({ label: '', code: '', gstRate: 18, description: '', sortOrder: '', isActive: true });
  const { data: m, loading, error, reload } = useFetch<MasterEntry | null>(() => (id ? masterService.get(id) : Promise.resolve(null)), [id]);
  useCrumb(`/masters/${type}`, section?.title);
  useCrumb(`/masters/${type}/${id}`, m?.label);

  useEffect(() => {
    if (m) setForm({ label: m.label, code: m.code ?? '', gstRate: m.gstRate ?? 18, description: m.description ?? '', sortOrder: String(m.sortOrder ?? ''), isActive: m.isActive ?? true });
  }, [m, setForm]);

  if (!section) return <Navigate to="/masters" replace />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (editing && loading && !m) return <PageLoader />;

  const isHsn = type === 'hsnSac';
  const isGst = type === 'gstRate';
  const back = () => navigate(`/masters/${type}`);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (isHsn && !editing && !/^\d{4,8}$/.test(form.code.trim())) errs.code = 'HSN / SAC codes are 4 to 8 digits';
    if (isGst && !editing && !(Number(form.gstRate) >= 0 && Number(form.gstRate) <= 28)) errs.gstRate = 'Enter a rate from 0 to 28';
    if (!isHsn && !isGst && !form.label.trim()) errs.label = 'Enter a name';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const label = isHsn ? form.code.trim() || form.label : isGst ? `${Number(form.gstRate)}%` : form.label.trim();
    const common = {
      label: editing ? form.label.trim() || label : label,
      description: trimOrUndef(form.description),
      ...(form.sortOrder !== '' ? { sortOrder: Number(form.sortOrder) } : {}),
      ...(isHsn ? { gstRate: Number(form.gstRate) } : {}),
    };
    setSaving(true);
    try {
      if (id) await masterService.update(id, { ...common, isActive: form.isActive });
      else await masterService.create({ ...common, type: type as MasterType, ...(isHsn ? { code: form.code.trim() } : {}), ...(isGst ? { gstRate: Number(form.gstRate) } : {}) });
      await refreshMasters();
      toast.success(editing ? 'Saved' : `${section.single[0].toUpperCase()}${section.single.slice(1)} added`);
      back();
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };

  const actions = (
    <>
      <Button variant="outline" onClick={back} disabled={saving}>Cancel</Button>
      <Button type="submit" form="master-form" leftIcon="check" loading={saving}>{editing ? 'Save' : 'Add'}</Button>
    </>
  );

  return (
    <>
      <PageHeader title={editing ? `Edit ${m?.label ?? ''}` : `Add ${section.single}`} subtitle={section.help} actions={actions} />
      <form id="master-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title={section.title}>
                {isHsn && (
                  <>
                    <Input label="HSN / SAC code" required autoFocus={!editing} inputMode="numeric" value={form.code} onChange={(e) => set('code')(e.target.value.replace(/\D/g, ''))} error={errors.code} disabled={editing} maxLength={8} hint={editing ? 'The code cannot change' : 'e.g. 8544 for wire, 995461 for installation work'} />
                    <Select label="Default GST" options={masterOptions('gstRate', { numeric: true, keep: Number(form.gstRate) })} value={Number(form.gstRate)} onChange={set('gstRate')} searchable={false} />
                    <Input label="Description" value={form.description} onChange={set('description')} maxLength={200} containerClassName="sm:col-span-2" placeholder="e.g. Insulated wires and cables" />
                  </>
                )}
                {isGst && (
                  <Input label="GST rate (%)" required autoFocus={!editing} inputMode="decimal" value={String(form.gstRate)} onChange={(e) => set('gstRate')(e.target.value.replace(/[^\d.]/g, ''))} error={errors.gstRate} disabled={editing} hint={editing ? 'The rate cannot change. Add a new one instead.' : undefined} />
                )}
                {!isHsn && !isGst && (
                  <>
                    <Input label="Name" required autoFocus value={form.label} onChange={set('label')} error={errors.label} containerClassName="sm:col-span-2" hint={editing ? 'Renaming updates the name everywhere; saved records keep working.' : undefined} />
                    <Textarea label="Note" rows={2} value={form.description} onChange={set('description')} maxLength={200} containerClassName="sm:col-span-2" placeholder="Optional" />
                  </>
                )}
                <Input label="Order in dropdowns" inputMode="numeric" value={form.sortOrder} onChange={(e) => set('sortOrder')(e.target.value.replace(/\D/g, ''))} placeholder="Last" hint="Smaller numbers show first" />
              </FormSection>
              {editing && (
                <Card className="p-5">
                  <Switch label="Show in dropdowns" description="Hidden entries stay on old records but can't be picked for new ones" checked={form.isActive} onChange={set('isActive')} />
                </Card>
              )}
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            editing && (
              <Card>
                <CardHeader title="About this entry" />
                <div className="p-5 pt-4">
                  <KeyValues
                    rows={[
                      ['Stored as', <span className="font-mono text-xs">{m?.value}</span>],
                      ['Used by', `${m?.usedBy ?? 0} record${m?.usedBy === 1 ? '' : 's'}`],
                      ['Built-in', m?.isSystem ? 'Yes (can be renamed or hidden)' : 'No'],
                    ]}
                  />
                </div>
              </Card>
            )
          }
        />
      </form>
    </>
  );
}
